import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, UserPlus, Stethoscope, Phone, Mail, Trash2, Pencil, Save, Loader2 } from "lucide-react";
import FormError from "./ui/FormError";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { confirmDialog } from "../utils/dialogs";

const EMPTY = { name: "", specialty: "", phone: "", email: "", notes: "" };

/**
 * Full CRUD modal for the shared doctors phonebook. Used both as a standalone
 * "manage doctors" screen and reused by ShareModal when the user wants to add
 * a doctor inline before sharing.
 *
 * Props:
 *   open       : bool
 *   onClose    : fn()
 *   onChanged  : fn()  — called after any add/edit/delete so parents can refresh
 */
export default function DoctorsManager({ open, onClose, onChanged }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEscClose(open, onClose);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/doctors");
      setDoctors(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load doctors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setError("");
      setForm(EMPTY);
      setEditingId(null);
      load();
    }
  }, [open]);

  const save = async () => {
    setError("");
    if (!form.name.trim()) {
      setError("Doctor name is required");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/doctors/${editingId}`, form);
      } else {
        await api.post("/doctors", form);
      }
      setForm(EMPTY);
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const edit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name || "",
      specialty: d.specialty || "",
      phone: d.phone || "",
      email: d.email || "",
      notes: d.notes || "",
    });
  };

  const remove = async (d) => {
    if (!(await confirmDialog(`Delete "${d.name}" from the phonebook?`, { confirmLabel: "Delete", danger: true }))) return;
    try {
      await api.delete(`/doctors/${d.id}`);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="doctors-manager-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="card w-full max-w-3xl p-5 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Stethoscope size={18} className="text-cyan-300" />
            <div className="font-display text-lg">Doctors phonebook</div>
            <span className="text-[11px] text-slate-500 font-mono-data">
              {doctors.length} contact{doctors.length === 1 ? "" : "s"}
            </span>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="doctors-close-btn">
            <X size={14} />
          </button>
        </div>

        {error && (
          <div className="mb-3">
            <FormError message={error} onDismiss={() => setError("")} testId="doctors-error" />
          </div>
        )}

        {/* Add / edit form */}
        <div className="card !bg-[#0B1627] p-4 mb-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-3">
            {editingId ? "Edit doctor" : "Add new doctor"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              placeholder="Name *"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="form-input"
              data-testid="doctor-form-name"
            />
            <input
              placeholder="Specialty (e.g. Endocrinology)"
              value={form.specialty}
              onChange={(e) => setForm({ ...form, specialty: e.target.value })}
              className="form-input"
              data-testid="doctor-form-specialty"
            />
            <input
              placeholder="Phone with country code (e.g. +919876543210)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="form-input"
              data-testid="doctor-form-phone"
            />
            <input
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="form-input"
              data-testid="doctor-form-email"
            />
            <input
              placeholder="Notes (clinic name, days available…)"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="form-input md:col-span-2"
              data-testid="doctor-form-notes"
            />
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            {editingId && (
              <button
                onClick={() => { setEditingId(null); setForm(EMPTY); }}
                className="pill"
                data-testid="doctor-form-cancel"
              >
                Cancel
              </button>
            )}
            <button
              onClick={save}
              disabled={busy}
              className="pill active disabled:opacity-50"
              data-testid="doctor-form-save"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : editingId ? <Save size={13} /> : <UserPlus size={13} />}
              {editingId ? "Update" : "Add doctor"}
            </button>
          </div>
          {error && (
            <div className="mt-2">
              <FormError message={error} onDismiss={() => setError("")} testId="doctors-error-near-save" />
            </div>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="py-10 text-center text-slate-500">Loading…</div>
        ) : doctors.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">
            No doctors yet. Add your first contact above.
          </div>
        ) : (
          <div className="divide-y divide-[#1E304B] border border-[#1E304B] rounded-lg">
            {doctors.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`doctor-row-${d.id}`}>
                <div className="min-w-[180px]">
                  <div className="text-slate-100 font-display text-sm">{d.name}</div>
                  {d.specialty && (
                    <div className="text-[10px] text-cyan-300/70 uppercase tracking-wider">{d.specialty}</div>
                  )}
                </div>
                {d.phone && (
                  <div className="text-[12px] text-slate-300 font-mono-data flex items-center gap-1">
                    <Phone size={11} className="text-emerald-300" /> {d.phone}
                  </div>
                )}
                {d.email && (
                  <div className="text-[12px] text-slate-300 font-mono-data flex items-center gap-1">
                    <Mail size={11} className="text-cyan-300" /> {d.email}
                  </div>
                )}
                {d.notes && (
                  <div className="text-[11px] text-slate-500 italic max-w-[220px] truncate">{d.notes}</div>
                )}
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={() => edit(d)}
                    className="pill !text-[11px] !py-1 !px-2"
                    data-testid={`doctor-edit-${d.id}`}
                  >
                    <Pencil size={11} /> Edit
                  </button>
                  <button
                    onClick={() => remove(d)}
                    className="pill !text-[11px] !py-1 !px-2"
                    style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
                    data-testid={`doctor-delete-${d.id}`}
                  >
                    <Trash2 size={11} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
